import { useSetToast } from "@app/components/Toast";
import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { FlexCol, FlexRow, type FlexProps } from "@design-system/Layout/Flex";
import { cn } from "@design-system/Utilities";
import {
    useEffect,
    useRef,
    useState,
    type ChangeEvent,
    type DragEvent,
    type FC,
    type FormEvent,
    type KeyboardEvent,
    type MouseEvent,
} from "react";

import { validateFileAgainstAccept } from "@app/shared/utils/file";
import { getFileFormatCategoryFromMimeType } from "@app/usecases/VersionUseCases/utils";
import { type HandleSubmitType } from "@app/utils/ai/chat";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Box } from "@design-system/Layout/Box";
import { ExtensionTag } from "../../ExtensionTag";
import { useAgentChatTranslations } from "./i18n";

export const Input: FC<
    Omit<FlexProps, "onSubmit"> & {
        input: string;
        onInputChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
        onSubmit: HandleSubmitType;
        onStop?: () => void;
        onFileChange?: (selectedFile: File | undefined) => void;
        acceptedFileTypes?: string;
        uploadedFile?: File | undefined;
        placeholder?: string;
        disabled?: boolean;
        loading?: boolean;
    }
> = ({
    className,
    disabled,
    onSubmit,
    onStop,
    placeholder,
    input,
    onInputChange,
    loading,
    onFileChange,
    acceptedFileTypes = ".pdf",
    uploadedFile,
    ...props
}) => {
    const { t, keys } = useAgentChatTranslations();
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const { setToastError } = useSetToast();

    const showPlaceholder = !input.length;

    const adjustTextareaHeight = () => {
        if (textAreaRef.current) {
            textAreaRef.current.style.height = "auto";
            const newHeight = Math.min(textAreaRef.current.scrollHeight, 200);
            textAreaRef.current.style.height = `${newHeight}px`;
        }
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        if (disabled) {
            return;
        }
        e.preventDefault();
        onSubmit(e);
        adjustTextareaHeight();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (disabled) {
            return;
        }
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSubmit(e);
            adjustTextareaHeight();
        }
    };

    const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        onInputChange(e);
        adjustTextareaHeight();
    };

    const resetFile = () => {
        onFileChange?.(undefined);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handlePaperclipClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const [file] = e.target.files ?? [];

        if (file && !validateFileAgainstAccept(file, acceptedFileTypes)) {
            setToastError(
                t(keys.file_type_not_supported, {
                    acceptedFileTypes,
                }),
            );
            e.target.value = "";
            return;
        }

        onFileChange?.(file);
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        if (!onFileChange) return;
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        if (!onFileChange) return;
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        if (!onFileChange) return;
        const [file] = e.dataTransfer.files ?? [];

        e.preventDefault();
        setIsDragOver(false);

        if (file && !validateFileAgainstAccept(file, acceptedFileTypes)) {
            setToastError(
                t(keys.file_type_not_supported, {
                    acceptedFileTypes,
                }),
            );
            return;
        }

        onFileChange(file);
    };

    const handleStopClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        onStop?.();
    };

    useEffect(() => {
        if (!input.length) {
            adjustTextareaHeight();
        }
    }, [input]);

    return (
        <form onSubmit={handleSubmit}>
            <FlexCol
                w="full"
                p="4"
                gap="2"
                br="lg"
                className={cn(
                    "w-[800px]",
                    "border border-tertiary",
                    onFileChange &&
                        isDragOver &&
                        "border-accent-1-500 border-2 border-dashed bg-accent-1-50",
                    className,
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                {...props}
            >
                {uploadedFile && (
                    <Box
                        className="flex h-10 w-10 border border-tertiary bg-white group relative"
                        p="1"
                        br="xl"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {uploadedFile.type ? (
                            <ExtensionTag
                                name={getFileFormatCategoryFromMimeType(
                                    uploadedFile.type,
                                )}
                                className="w-7 h-7"
                            />
                        ) : (
                            <Box
                                w="7"
                                minH="7"
                                minW="7"
                                h="7"
                                br="full"
                                className="flex items-center justify-center"
                            >
                                <Icon name="paperclip" size="sm" />
                            </Box>
                        )}
                        <Icon
                            name="close"
                            size="sm"
                            className="absolute top-1 right-1 cursor-pointer text-white bg-black rounded-full p-1"
                            onClick={resetFile}
                        />
                    </Box>
                )}
                <textarea
                    className={cn(
                        "border-none resize-none focus:ring-0 p-0",
                        "text-lg text-primary",
                        "placeholder:text-lg placeholder:text-tertiary",
                    )}
                    placeholder={
                        showPlaceholder
                            ? (placeholder ?? t(keys.ask_me_anything))
                            : undefined
                    }
                    ref={textAreaRef}
                    onKeyDown={handleKeyDown}
                    onInput={handleInputChange}
                    value={input}
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                />
                <FlexRow
                    w="full"
                    justifyContent={onFileChange ? "between" : "end"}
                >
                    {onFileChange && (
                        <Box>
                            <input
                                type="file"
                                className="hidden"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                onClick={resetFile}
                                accept={acceptedFileTypes}
                            />
                            <Tooltip>
                                <Tooltip.Trigger asChild>
                                    <Button
                                        type="button"
                                        variant="text"
                                        size="sm"
                                        onClick={handlePaperclipClick}
                                    >
                                        <Button.Icon name="paperclip" />
                                    </Button>
                                </Tooltip.Trigger>
                                <Tooltip.Content>
                                    {t(keys.tooltip_upload_file)}
                                </Tooltip.Content>
                            </Tooltip>
                        </Box>
                    )}
                    {onStop ? (
                        <Button
                            className="rounded-[120px] min-h-[32px] min-w-[32px]"
                            variant="outlined"
                            type="button"
                            size="sm"
                            onClick={handleStopClick}
                        >
                            <Button.Icon name="stop" size="md" />
                        </Button>
                    ) : (
                        <Button
                            className="rounded-[120px] min-h-[32px] min-w-[32px]"
                            disabled={disabled}
                            loading={loading}
                            variant="flatAccent"
                            size="sm"
                            type="submit"
                        >
                            <Button.Icon name="arrowUp" />
                        </Button>
                    )}
                </FlexRow>
            </FlexCol>
        </form>
    );
};
